import { Injectable } from '@angular/core';
import { UiToast, UiToastVariant } from './ui-toast.types';

export interface UiToastOptions {
  title?: string;
  variant?: UiToastVariant;
  durationMs?: number;
  closable?: boolean;
  className?: string;

  /**
   * Max number of toasts kept in memory.
   * If exceeded, the oldest toast is removed.
   */
  maxStack?: number;
}

@Injectable({ providedIn: 'root' })
export class UiToastService {
  /** Rendered by UiToastComponent. */
  toasts: UiToast[] = [];

  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  private readonly defaultDurationMs = 4000;
  private readonly defaultMaxStack = 5;

  show(message: string, options: UiToastOptions = {}): string {
    const id = this.generateId();

    const variant = options.variant ?? 'neutral';
    const durationMs = options.durationMs ?? this.defaultDurationMs;
    const closable = options.closable ?? durationMs === 0;
    const maxStack = options.maxStack ?? this.defaultMaxStack;

    const toast: UiToast = {
      id,
      title: options.title,
      message,
      variant,
      durationMs,
      closable,
      className: options.className,
      createdAt: Date.now(),
    };

    this.toasts = [toast, ...this.toasts];

    // Enforce stack limit (remove oldest)
    if (this.toasts.length > maxStack) {
      const removed = this.toasts.slice(maxStack);
      this.toasts = this.toasts.slice(0, maxStack);
      removed.forEach(t => this.clearTimer(t.id));
    }

    if (durationMs > 0) {
      const timer = setTimeout(() => this.dismiss(id), durationMs);
      this.timers.set(id, timer);
    }

    return id;
  }

  success(message: string, options: UiToastOptions = {}): string {
    return this.show(message, { ...options, variant: 'success' });
  }

  error(message: string, options: UiToastOptions = {}): string {
    return this.show(message, { ...options, variant: 'error' });
  }

  warning(message: string, options: UiToastOptions = {}): string {
    return this.show(message, { ...options, variant: 'warning' });
  }

  info(message: string, options: UiToastOptions = {}): string {
    return this.show(message, { ...options, variant: 'info' });
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  clear(): void {
    Array.from(this.timers.keys()).forEach(id => this.clearTimer(id));
    this.toasts = [];
  }

  private clearTimer(id: string) {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  private generateId(): string {
    return 'toast-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
  }
}

